import styled from '@emotion/styled';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useState } from 'react';
import Image from 'src/components/image';
import { SHOW_COMMEMORATE } from 'src/constants';
import { GET } from 'src/services';

const WidgetByline = ({ data }) => {
  const DEFAULT_BYLINE = [
    {
      p_name: 'กรุงเทพธุรกิจ',
      description: `
        <p>สื่อเศรษฐกิจ ที่อยู่กับการเปลี่ยนแปลงของประเทศมาอย่างยาวนาน</p>
        <p>ถ่ายทอดข้อมูลที่แม่นยำ รอบด้าน เพื่อให้ผู้บริหาร นักลงทุน ผู้ประกอบการ</p>
        <p>ตัดสินใจบนพื้นฐานของ “ข้อเท็จจริง” และ “โอกาส”</p>
      `,
      image: '/images/default.jpg'
    }
  ];

  const [bylineData, setBylineData] = useState(() => (data?.length > 0 ? data : DEFAULT_BYLINE));

  useEffect(() => {
    if (!isEmpty(data)) {
      setBylineData(data);
      return;
    }

    let active = true;
    (async () => {
      try {
        const res = await GET(`/api/v1.0/bylines/contents/${encodeURIComponent('กรุงเทพธุรกิจ')}`, false, true);
        if (!active) return;
        const byline = res?.byline;

        if (!isEmpty(byline)) {
          setBylineData([
            {
              p_name: byline.p_name,
              description: byline.description,
              image: byline.image
            }
          ]);
        }
      } catch (err) {
        if (!active) return;
        // keep default placeholder if fetch fails
        setBylineData(DEFAULT_BYLINE);
      }
    })();

    return () => {
      active = false;
    };
  }, [data]);

  if (isEmpty(bylineData)) return null;

  return (
    <WidgetWrapper className='byline-body' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
      {bylineData.map((item, index) => {
        const hasLink = !isEmpty(item?.p_name);
        const CardTag = hasLink ? 'a' : 'div';
        const href = hasLink ? `/bylines/${String(item.p_name).split('/').map(encodeURIComponent).join('/')}` : undefined;
        const linkProps = hasLink ? { href, 'aria-label': `ดูบทความของ ${item.p_name}` } : {};

        return (
          <React.Fragment key={index}>
            <CardWrapper>
              <CardTag {...linkProps} className={`card-link ${hasLink ? '' : 'no-link'}`}>
                <div className='byline-header'>
                  <div className='avatar-wrapper'>
                    <Image type='CARD_SMALL' width='72px' height='72px' className='byline-img' image={item?.image || '/images/default.jpg'} title={item?.p_name} />
                  </div>

                  <div className='byline-info'>
                    <p className='byline-name'>{item?.p_name}</p>
                    {item?.position && <p className='byline-position'>{item.position}</p>}
                  </div>
                </div>

                {!isEmpty(item?.description) && <div className='byline-desc' dangerouslySetInnerHTML={{ __html: item.description }} />}
                {/* <div className='byline-desc'>แสดงเลย ไม่ต้องกด</div> */}
              </CardTag>
            </CardWrapper>
          </React.Fragment>
        );
      })}
    </WidgetWrapper>
  );
};

/* ------------------------------------------------------------------ */
/*  Styled Components                                                   */
/* ------------------------------------------------------------------ */

const CardWrapper = styled.div`
  position: relative;
  border-left: 6px solid var(--quaternary-color);
  background: #f4f7ff;
  margin: 16px auto;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition:
    box-shadow 0.25s ease,
    transform 0.25s ease;
    cursor: pointer;

  .byline-img {
    width: 72px;
    height: 72px;
  }

  &:hover {
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);

    opacity: 0.7;
  }

  .card-link {
    display: block;
    padding: 20px 20px 20px 22px;
    text-decoration: none;
    color: inherit;

    &.no-link {
      cursor: default;
      pointer-events: none;
    }
  }

  .byline-header {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .avatar-wrapper {
    position: relative;
    flex-shrink: 0;
    width: 72px !important;
    height: 72px !important;
    img {
      display: block !important;
      width: 72px !important;
      height: 72px !important;
      aspect-ratio: 1 / 1 !important;
      margin: 0 !important;
      margin-top: 0 !important;
      max-height: unset !important;
      max-width: unset !important;
      border-radius: 50%;
      object-fit: cover;
      padding: 2px;
      border: 2px solid var(--quaternary-color);
    }
  }

  .avatar-dot {
    position: absolute;
    bottom: 3px;
    right: 3px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 2px solid #fff;
  }

  .byline-info {
    flex: 1;
  }

  .byline-name {
    font-size: 28px;
    font-weight: 700;
    margin: 0 0 4px;
    /* ✅ climatecenter → #4aaf7f / อื่นๆ → primary-color */
    color: var(--secondary-blue);
    line-height: 1.3;
    @media (max-width: 768px) {
      font-size: 25px;
    }
  }

  .byline-position {
    font-size: 13px;
    color: #888;
    margin: 0;
  }

  .byline-desc {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid rgba(0, 0, 0, 0.07);
    font-size: 24px;
    line-height: 1.75;
    color: #444;
    @media (max-width: 768px) {
      font-size: 24px;
    }
    p {
      margin: 0 0 8px;
    }
    p:last-child {
      margin-bottom: 0;
    }

    a {
      color: var(--quaternary-color);
      text-decoration: underline;
    }
  }
`;

const WidgetWrapper = styled.div`
  width: 100%;
`;

export default WidgetByline;
